import { Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { filter, map, pairwise } from 'rxjs';

/**

 * Creates a signal that emits values from the source signal only when the filter function returns true.
 * The filter function receives the previous and next values as a tuple.
 *
 * @param sourceSignal - The source signal to observe
 * @param filterFn - A function that takes [previous, next] values and returns true if the next value should be emitted
 * @returns A signal that emits filtered values
 *
 * @example
 * ```typescript
 * // Filter when _id changes
 * const filteredSignal = filterSignalPairwise(
 *   this.selectedTranscript,
 *   ([prev, next]) => prev?._id !== next?._id
 * );
 * ```
 */
export function filterSignalWithPrevious<T>(
  sourceSignal: Signal<T>,
  filterFn: (previous: T, next: T) => boolean
): Signal<T | undefined> {
  return toSignal(
    toObservable(sourceSignal).pipe(
      pairwise(),
      filter(([previous, next]) => filterFn(previous, next)),
      map(([, next]) => next)
    )
  );
}
/**
 * Creates a signal that emits only non-null values from the given signal.
 * @param signal A signal that can be null
 * @returns A signal that is not null
 */
export function createNonNullSignal<T>(signal: Signal<T | null>) {
  return toSignal(toObservable(signal).pipe(filter((value): value is T => value !== null)));
}
